import { createSlice } from '@reduxjs/toolkit';
import { MfaStatusType } from '@upstart/harmony/lib/legos/constants/user';
import {
  isDtoValidationError,
  isStandardError,
} from '@upstart/harmony/lib/backend-validation-utils';
import AuthService from '../../services/AuthService';
import { User } from '../../types/userTypes';
import { setFetching } from '../utils';
import {
  mfaLogin,
  fetchCurrentUser,
  login,
  resendOTP,
  verifyOTP,
} from './thunks';

const UNKNOWN_ERR_MSG =
  'There was an issue trying to login, please try again later or contact support for assistance.';

export const authSlice = createSlice({
  name: 'auth',
  initialState: {
    fetching: false,
    token: AuthService.getToken(),
    currentUser: null as User | null,
    mfaLoginToken: '',
    legacyLoginDisabled: undefined as boolean | undefined,
    error: {
      message: '',
      status: '',
    },
    // This state attribute is temporary, should be removed when Skip Authentication feature is removed
    credentials: {
      email: '',
      password: '',
    },
  },
  reducers: {
    logout: (state) => {
      state.token = null;
      state.currentUser = null;
      AuthService.logout();
      AuthService.setToken(null);
      state.mfaLoginToken = '';
      state.error.message = '';
      state.error.status = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.pending, setFetching());
    builder.addCase(login.rejected, (state, action) => {
      const errorPayload: any = action.payload;
      state.fetching = false;
      if (errorPayload?.data?.status) {
        state.error.status = errorPayload.data.status;
      }
      state.error.message = errorPayload.data.error;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.fetching = false;
      state.token = action.payload.token;
      state.currentUser = action.payload;
      AuthService.setToken(action.payload.token);
      state.credentials.email = '';
      state.credentials.password = '';
    });

    builder.addCase(mfaLogin.pending, (state, action) => {
      const { arg } = action.meta;

      if ('email' in arg && 'password' in arg) {
        state.credentials.email = arg.email;
        state.credentials.password = arg.password;
      }

      state.fetching = true;
    });
    builder.addCase(mfaLogin.rejected, (state, action) => {
      const errorPayload: any = action.payload;
      state.fetching = false;
      const generalMsg =
        "It looks like the login isn't quite right. Check your launch email for the correct login details.";
      state.credentials.email = '';
      state.credentials.password = '';
      if (errorPayload?.data?.status) {
        state.error.status = errorPayload.data.status;
      }

      const errorRsp = errorPayload.data;
      if (errorPayload.status >= 500) {
        state.error.message = UNKNOWN_ERR_MSG;
      } else if (isStandardError(errorRsp)) {
        state.error.message = errorRsp.error;
      } else if (isDtoValidationError(errorRsp)) {
        state.error.message = Object.values(errorRsp.error).join(' ');
      } else {
        state.error.message = generalMsg;
      }
    });
    builder.addCase(mfaLogin.fulfilled, (state, action) => {
      const { payload } = action;
      state.fetching = false;
      if (payload.status === MfaStatusType.SUCCESS) {
        const userToken = payload.user.token ? payload.user.token : null;
        state.token = userToken;
        state.currentUser = payload.user;
        AuthService.setToken(userToken);
        state.mfaLoginToken = '';
        state.error.message = '';
        state.error.status = '';
        state.credentials.email = '';
        state.credentials.password = '';
      } else if (payload.status === MfaStatusType.MFA_REQUIRED) {
        AuthService.setMfaSessionToken(null);
        state.mfaLoginToken = payload.mfaToken;
        state.legacyLoginDisabled = payload.legacyLoginDisabled;
        state.error.status = '';
        state.error.message = '';
      }
    });

    builder.addCase(fetchCurrentUser.pending, setFetching());
    builder.addCase(fetchCurrentUser.rejected, setFetching(false));
    builder.addCase(fetchCurrentUser.fulfilled, (state, action) => {
      state.fetching = false;
      state.currentUser = action.payload;
    });

    builder.addCase(verifyOTP.pending, (state, action) => {
      const { arg } = action.meta;
      if (!arg.rememberDevice) {
        AuthService.setMfaSessionToken(null);
      }

      state.fetching = true;
    });
    builder.addCase(verifyOTP.rejected, (state, action) => {
      const errorPayload: any = action.payload;
      state.fetching = false;
      if (errorPayload?.data?.status) {
        state.error.status = errorPayload.data.status;
      }
      if (
        errorPayload.data.status === MfaStatusType.TOKEN_EXPIRED ||
        errorPayload.data.status === MfaStatusType.TOKEN_INVALID
      ) {
        state.mfaLoginToken = '';
      }

      const errorRsp = errorPayload.data;
      if (isStandardError(errorRsp)) {
        state.error.message = errorRsp.error;
      } else if (isDtoValidationError(errorRsp)) {
        state.error.message = Object.values(errorRsp.error).join(' ');
      } else {
        state.error.message = UNKNOWN_ERR_MSG;
      }
    });
    builder.addCase(verifyOTP.fulfilled, (state, action) => {
      const { payload } = action;
      const { arg } = action.meta;
      state.fetching = false;

      if (payload.status === MfaStatusType.SUCCESS) {
        if (arg.rememberDevice) {
          AuthService.setMfaSessionToken(payload.mfaToken);
        }
        const userToken = payload.user.token ? payload.user.token : null;
        state.token = userToken;
        state.currentUser = payload.user;
        AuthService.setToken(userToken);
        state.mfaLoginToken = '';
        state.error.status = '';
        state.error.message = '';
      }
    });

    builder.addCase(resendOTP.pending, setFetching());
    builder.addCase(resendOTP.rejected, (state, action) => {
      const errorPayload: any = action.payload;
      state.fetching = false;
      if (errorPayload?.data?.status) {
        state.error.status = errorPayload.data.status;
      }
      if (
        errorPayload.data.status === MfaStatusType.TOKEN_EXPIRED ||
        errorPayload.data.status === MfaStatusType.TOKEN_INVALID
      ) {
        state.mfaLoginToken = '';
      }
      state.error.message = errorPayload.data.error;
    });
    builder.addCase(resendOTP.fulfilled, setFetching(false));
  },
});
